import { Fragment, useEffect, useRef, useState } from "react";
import { GripVertical, Plus, Trash2, Shield, Check, Search } from "lucide-react";
import { useStructureCtx } from "@/context/StructureContext";
import { StructureHeader } from "@/components/StructureHeader";
import { DraggableList } from "@/components/DraggableList";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const labelC = "mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]";
const inputC = "border-[#131620] bg-[#131620] text-white";

// Matrice permessi globali: ruoli in colonna, permessi in riga.
// Colonna dei permessi e riga dei ruoli restano fisse; il ruolo selezionato è evidenziato.
function RolePermMatrix({ roles, groups, onToggle, selUid, onSelect }) {
  const wrapRef = useRef(null);

  // Porta in vista la colonna del ruolo selezionato.
  useEffect(() => {
    const wrap = wrapRef.current;
    const th = wrap?.querySelector(`[data-col="${selUid}"]`);
    if (!wrap || !th) return;
    const stickyW = wrap.querySelector("th")?.offsetWidth || 0;
    const left = th.offsetLeft - stickyW - 16;
    const right = th.offsetLeft + th.offsetWidth - wrap.clientWidth + 16;
    if (wrap.scrollLeft > left) wrap.scrollTo({ left, behavior: "smooth" });
    else if (wrap.scrollLeft < right) wrap.scrollTo({ left: right, behavior: "smooth" });
  }, [selUid]);

  const colBg = (uid) => (uid === selUid ? "bg-[#5865F2]/10" : "");

  return (
    <div ref={wrapRef} className="max-h-[70vh] overflow-auto rounded-lg border border-[#232838]" data-testid="roles-perm-matrix">
      <table className="w-full border-separate border-spacing-0 text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-30 min-w-[170px] border-b border-r border-[#232838] bg-[#131620] p-2 text-left font-semibold text-[#949BA4] sm:min-w-[230px]">Permesso</th>
            {roles.map((r) => (
              <th key={r.uid} data-col={r.uid} onClick={() => onSelect(r.uid)}
                className={`sticky top-0 z-20 min-w-[84px] cursor-pointer border-b p-2 text-center ${r.uid === selUid ? "border-[#5865F2] bg-[#232a4d]" : "border-[#232838] bg-[#131620] hover:bg-[#1b1f2e]"}`}>
                <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: r.color }} />
                  <span className="max-w-[90px] truncate font-medium text-white" title={r.name}>{r.name}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.category}>
              <tr><td colSpan={roles.length + 1} className="bg-[#0f111a] p-0"><span className="sticky left-0 block w-fit px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#4b4d54]">{g.category}</span></td></tr>
              {g.items.map((p) => (
                <tr key={p.value} className="group">
                  <td className="sticky left-0 z-10 border-r border-t border-[#232838] bg-[#161923] p-2 text-[#DBDEE1] group-hover:bg-[#1b1f2e]" title={p.description || p.label}>{p.label}</td>
                  {roles.map((r) => {
                    const on = (r.permissions || []).includes(p.value);
                    return (
                      <td key={r.uid} className={`border-t border-[#232838] p-1 text-center ${colBg(r.uid)}`}>
                        <button onClick={() => onToggle(r.uid, p.value)} data-testid={`role-perm-${r.uid}-${p.value}`}
                          className={`inline-flex h-7 w-7 items-center justify-center rounded transition-colors ${on ? "bg-[#23A55A] text-white" : "bg-[#131620] text-[#4b4d54] hover:bg-[#252B3D]"}`}>
                          {on ? <Check className="h-4 w-4" /> : ""}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Roles() {
  const s = useStructureCtx();
  const { roles, meta } = s;
  const [selUid, setSelUid] = useState(null);
  const [q, setQ] = useState("");
  const [onlySel, setOnlySel] = useState(false);

  const nonEveryone = roles.filter((r) => !r.isEveryone);
  const everyone = roles.filter((r) => r.isEveryone);

  useEffect(() => {
    if ((!selUid || !roles.some((r) => r.uid === selUid)) && nonEveryone[0]) setSelUid(nonEveryone[0].uid);
  }, [roles, nonEveryone, selUid]);
  const sel = roles.find((r) => r.uid === selUid) || null;

  const togglePerm = (uid, perm) => {
    const r = roles.find((x) => x.uid === uid); if (!r) return;
    const has = (r.permissions || []).includes(perm);
    s.updRole(uid, { permissions: has ? r.permissions.filter((x) => x !== perm) : [...(r.permissions || []), perm] });
  };

  const term = q.trim().toLowerCase();
  const match = (r) => !term || r.name.toLowerCase().includes(term);
  const matrixRoles = onlySel && sel ? [sel] : roles.filter(match);

  const rowClass = (active) => `group flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer ${active ? "bg-[#5865F2]/20 ring-1 ring-[#5865F2]/40" : "bg-[#131620] hover:bg-[#1b1f2e]"}`;

  return (
    <div className="space-y-5 pb-20">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Ruoli &amp; permessi</h1>
        <p className="text-sm text-slate-400">Gerarchia dei ruoli e permessi globali. Trascina per riordinare; le modifiche vanno su Discord quando premi «Salva».</p>
      </div>
      <StructureHeader s={s} tab="ruoli" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
        {/* ── lista ruoli ── */}
        <Card className="min-w-0 border-[#232838] bg-[#161923] p-3">
          <Button size="sm" onClick={() => { setQ(""); setSelUid(s.addRole()); }} data-testid="add-role-btn" className="mb-2 w-full bg-[#5865F2] hover:bg-[#4752C4]"><Plus className="mr-1 h-4 w-4" />Nuovo ruolo</Button>
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#949BA4]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un ruolo…" className={`${inputC} h-9 pl-8`} data-testid="roles-search" />
          </div>
          <p className="px-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-[#4b4d54]">Gerarchia (dall'alto = più potere)</p>
          <div className="max-h-[62vh] space-y-1.5 overflow-y-auto pr-1">
            {term ? (
              nonEveryone.filter(match).map((r) => (
                <div key={r.uid} className={rowClass(selUid === r.uid)} onClick={() => setSelUid(r.uid)}>
                  <span className="h-3 w-3 rounded-full" style={{ background: r.color }} />
                  <span className="flex-1 truncate font-medium text-white">{r.name}</span>
                </div>
              ))
            ) : (
              <DraggableList items={nonEveryone} getKey={(r) => r.uid} onReorder={(re) => s.setRoles([...re, ...everyone])}>
                {(r) => (
                  <div className={rowClass(selUid === r.uid)} onClick={() => setSelUid(r.uid)} data-testid={`role-node-${s.slug(r.name)}`}>
                    <GripVertical className="h-4 w-4 cursor-grab text-[#4b4d54]" />
                    <span className="h-3 w-3 rounded-full" style={{ background: r.color }} />
                    <span className="flex-1 truncate font-medium text-white">{r.name}</span>
                    <span className="rounded bg-[#0f111a] px-1.5 py-0.5 font-mono text-[10px] text-[#949BA4]" title="Permessi attivi">{(r.permissions || []).length}</span>
                    <button onClick={(e) => { e.stopPropagation(); s.delRole(r.uid); if (selUid === r.uid) setSelUid(null); }} title="Elimina ruolo" className="text-[#4b4d54] opacity-0 hover:text-[#F23F43] group-hover:opacity-100"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </DraggableList>
            )}
            {everyone.filter(match).map((r) => (
              <div key={r.uid} className={rowClass(selUid === r.uid)} onClick={() => setSelUid(r.uid)} data-testid="role-node-everyone">
                <Shield className="h-4 w-4 text-[#4b4d54]" />
                <span className="flex-1 truncate font-medium text-[#949BA4]">{r.name}</span>
                <span className="rounded bg-[#0f111a] px-1.5 py-0.5 font-mono text-[10px] text-[#949BA4]">{(r.permissions || []).length}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* ── dettaglio + matrice ── */}
        <div className="min-w-0 space-y-5">
          {sel && (
            <Card className="border-[#232838] bg-[#161923] p-5" data-testid="role-detail">
              <div className="grid grid-cols-[1fr_auto] items-end gap-4">
                <div className="min-w-0"><label className={labelC}>Nome del ruolo</label><Input value={sel.name} disabled={sel.isEveryone} onChange={(e) => s.updRole(sel.uid, { name: e.target.value })} data-testid="role-name-input" className={inputC} /></div>
                <div><label className={labelC}>Colore</label><input type="color" value={sel.color} disabled={sel.isEveryone} onChange={(e) => s.updRole(sel.uid, { color: e.target.value })} className="h-9 w-16 cursor-pointer rounded border border-[#131620] bg-[#131620]" data-testid="role-color-input" /></div>
              </div>
              {!sel.isEveryone && (
                <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
                  <label className="flex items-center gap-2 text-sm text-[#B5BAC1]"><Switch checked={sel.hoist} onCheckedChange={(v) => s.updRole(sel.uid, { hoist: v })} data-testid="role-hoist-switch" />Mostra separatamente nella lista membri</label>
                  <label className="flex items-center gap-2 text-sm text-[#B5BAC1]"><Switch checked={sel.mentionable} onCheckedChange={(v) => s.updRole(sel.uid, { mentionable: v })} data-testid="role-mentionable-switch" />Tutti possono menzionarlo (@ruolo)</label>
                </div>
              )}
            </Card>
          )}
          <Card className="border-[#232838] bg-[#161923] p-5">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-white">Permessi globali</h3>
                <p className="text-xs text-[#949BA4]">Verde = concesso in tutto il server. Clicca il nome di un ruolo per selezionarlo.</p>
              </div>
              {sel && <label className="flex items-center gap-2 text-sm text-[#B5BAC1]"><Switch checked={onlySel} onCheckedChange={setOnlySel} data-testid="roles-only-selected" />Solo «{sel.name}»</label>}
            </div>
            {roles.length === 0 ? <p className="text-sm text-[#4b4d54]">Nessun ruolo. Aggiungine uno o ricarica dal server.</p>
              : <RolePermMatrix roles={matrixRoles} groups={meta?.permissions || []} onToggle={togglePerm} selUid={selUid} onSelect={setSelUid} />}
          </Card>
        </div>
      </div>
    </div>
  );
}
